import { VIZ_LIMITS, VIZ_PROTOCOL } from './limits.ts';

/**
 * The messages between the host page, the runner page (a sandboxed, opaque-origin iframe) and the
 * runner's Worker (ADR 0013). Every receiver validates the exact shape: unknown types, extra
 * fields that do not fit, a wrong protocol version or an oversized payload are dropped.
 *
 *   runner → host (window.postMessage, to the host's origin)  { v, type: 'cortex-viz:ready' }
 *                                                           or { v, type: 'cortex-viz:refused' }
 *   host → runner (window.postMessage + a transferred port)    { v, type: 'cortex-viz:connect' }
 *   host → runner (the port)                                   RenderRequest
 *   runner → host (the port)                                   RenderResult
 */
export const READY = 'cortex-viz:ready';
export const CONNECT = 'cortex-viz:connect';
/** The runner found itself served without its policy: it runs nothing. */
export const REFUSED = 'cortex-viz:refused';

export interface RenderRequest {
  v: typeof VIZ_PROTOCOL;
  type: 'render';
  /** Per render, from the host; the result must carry it back. */
  nonce: string;
  code: string;
  data: unknown;
  width: number;
  height: number;
}

export type RenderFailureKind = 'error' | 'timeout' | 'too_large' | 'no_svg' | 'unsafe_runner' | 'crashed';

export type RenderResult =
  | {
      v: typeof VIZ_PROTOCOL;
      type: 'result';
      nonce: string;
      ok: true;
      svg: string;
      elements: number;
      ms: number;
    }
  | {
      v: typeof VIZ_PROTOCOL;
      type: 'result';
      nonce: string;
      ok: false;
      error: { kind: RenderFailureKind; message: string; line?: number };
      ms: number;
    };

/** What the worker gets and gives back (inside the runner). */
export interface WorkerJob {
  code: string;
  data: unknown;
  width: number;
  height: number;
}
export type WorkerReply =
  | { type: 'done'; svg: string; elements: number }
  | {
      type: 'failed';
      kind: 'error' | 'no_svg' | 'too_large' | 'unsafe_runner';
      message: string;
      line?: number;
    };

const NONCE = /^[A-Za-z0-9-]{16,64}$/;
const isObj = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const keysAre = (o: Record<string, unknown>, allowed: string[]) =>
  Object.keys(o).every((k) => allowed.includes(k));
const num = (v: unknown, min: number, max: number) =>
  typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max;

export const isReady = (m: unknown) =>
  isObj(m) && m.v === VIZ_PROTOCOL && m.type === READY && keysAre(m, ['v', 'type']);
export const isRefused = (m: unknown) =>
  isObj(m) && m.v === VIZ_PROTOCOL && m.type === REFUSED && keysAre(m, ['v', 'type']);
export const isConnect = (m: unknown) =>
  isObj(m) && m.v === VIZ_PROTOCOL && m.type === CONNECT && keysAre(m, ['v', 'type']);

/** The runner's check of a render request from the host. */
export function parseRenderRequest(m: unknown): RenderRequest | null {
  if (!isObj(m) || m.v !== VIZ_PROTOCOL || m.type !== 'render') return null;
  if (!keysAre(m, ['v', 'type', 'nonce', 'code', 'data', 'width', 'height'])) return null;
  if (typeof m.nonce !== 'string' || !NONCE.test(m.nonce)) return null;
  if (typeof m.code !== 'string' || !m.code || m.code.length > VIZ_LIMITS.codeChars) return null;
  if (!num(m.width, VIZ_LIMITS.width.min, VIZ_LIMITS.width.max)) return null;
  if (!num(m.height, VIZ_LIMITS.height.min, VIZ_LIMITS.height.max)) return null;
  return m as unknown as RenderRequest;
}

/** The host's check of a result from the runner, for one pending nonce. */
export function parseRenderResult(m: unknown): RenderResult | null {
  if (!isObj(m) || m.v !== VIZ_PROTOCOL || m.type !== 'result') return null;
  if (typeof m.nonce !== 'string' || !NONCE.test(m.nonce) || !num(m.ms, 0, 3_600_000)) return null;
  if (m.ok === true) {
    if (!keysAre(m, ['v', 'type', 'nonce', 'ok', 'svg', 'elements', 'ms'])) return null;
    if (typeof m.svg !== 'string' || m.svg.length > VIZ_LIMITS.svgChars) return null;
    if (!num(m.elements, 0, VIZ_LIMITS.svgElements)) return null;
    return m as unknown as RenderResult;
  }
  if (m.ok !== false || !keysAre(m, ['v', 'type', 'nonce', 'ok', 'error', 'ms']) || !isObj(m.error))
    return null;
  const e = m.error;
  if (!keysAre(e, ['kind', 'message', 'line'])) return null;
  if (!['error', 'timeout', 'too_large', 'no_svg', 'unsafe_runner', 'crashed'].includes(e.kind as string))
    return null;
  if (typeof e.message !== 'string' || e.message.length > 2000) return null;
  if (e.line !== undefined && !num(e.line, 1, 1_000_000)) return null;
  return m as unknown as RenderResult;
}

/** The runner's check of the worker's reply. */
export function parseWorkerReply(m: unknown): WorkerReply | null {
  if (!isObj(m)) return null;
  if (m.type === 'done') {
    if (!keysAre(m, ['type', 'svg', 'elements']) || typeof m.svg !== 'string') return null;
    if (!num(m.elements, 0, Number.MAX_SAFE_INTEGER)) return null;
    return m as unknown as WorkerReply;
  }
  if (m.type === 'failed') {
    if (!keysAre(m, ['type', 'kind', 'message', 'line'])) return null;
    if (!['error', 'no_svg', 'too_large', 'unsafe_runner'].includes(m.kind as string)) return null;
    if (typeof m.message !== 'string') return null;
    if (m.line !== undefined && !num(m.line, 1, 1_000_000)) return null;
    return m as unknown as WorkerReply;
  }
  return null;
}
